import { Theme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import FilesClient from '../../clients/FilesClient';
import { ENTITIES, FOLDER_FILE_KEY, User } from '../../constants';
import { InboxPageContext } from '../../context/inboxPageContext';
import {
  useGetInboxNotificationsQuery,
  useGetInboxNotificationsByGroupIdQuery,
} from '../../services/api/inboxApi';
import { GraySmall, NonHoverBackground } from '../../theme/colors';
import { getUnreadInboxNotificationCount } from '../../utils/NotificationsUtils';
import {
  getExtensionFromFileKey,
  getFileNameFromFileKey,
} from '../Files/helpers';
import { NotificationIcon } from '../Icons';
import BaseTypography from '../Text/BaseTypography';
import { InboxNotificationTable } from './InboxNotificationTable';

export interface NotificationTableRowData extends User {
  fileName: string;
  fileLocation: string;
  channelId: string;
  userName: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    notificationIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 40,
      width: 40,
      backgroundColor: NonHoverBackground,
      borderRadius: 4,
      marginBottom: theme.spacing(1.5),
    },
    notificationCountDescription: {
      color: GraySmall,
    },
    container: {
      width: '100%',
      padding: theme.spacing(3, 4.5),
    },
  }),
);

export const InboxContent = () => {
  const classes = useStyles();
  const { data: allInboxNotifications } = useGetInboxNotificationsQuery();
  const { selectedNotification } = React.useContext(InboxPageContext);
  const isFileUploadNotification =
    selectedNotification &&
    selectedNotification.notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED;

  const {
    data: notificationsFromGroup,
  } = useGetInboxNotificationsByGroupIdQuery(
    { groupId: selectedNotification?.id as string },
    {
      // we only want to run query once a notfication is selected
      // and selected notification is a file upload notifcation
      // because notification grouping is done for NOTIFY_FILE_UPLOAD notifications
      skip: !isFileUploadNotification || !selectedNotification.id,
    },
  );

  const [
    notificationTableRowData,
    setNotificationTableRowData,
  ] = React.useState<NotificationTableRowData[]>([]);

  /**
   * generate row data for notification table
   */
  const generateNotificationTableRowData = React.useCallback(async () => {
    // don't do anything if no notification is selected

    if (!selectedNotification || !selectedNotification.channelId) return;

    const allChannelFiles = await FilesClient.GetFilesForChannelId(
      selectedNotification.channelId,
    );

    const data: NotificationTableRowData[] = [];
    // get id's of file related to notification
    let notificationFileIds: string[] = [];

    // for file upload notifications
    // we can retreive file id's from notifications fetched from groupID
    if (isFileUploadNotification) {
      notificationFileIds = notificationsFromGroup
        ? notificationsFromGroup.map(
            n => n.data.split('/')[2], // data string is divided into 3 parts by "/". `${Some_id}/${notification_status}/${fileId}`
          )
        : [];
    } else {
      // for notifications which are not grouped
      // we know there will be only 1 file in those notifications
      notificationFileIds = [selectedNotification.data.split('/')[2]];
    }

    const notificationFiles = allChannelFiles.filter(n =>
      notificationFileIds?.includes(n.id),
    );

    notificationFiles.forEach(file => {
      const { fields, owner } = file;

      // avoiding folders
      if (fields.fileKey === FOLDER_FILE_KEY) return;
      if (!owner) return;

      // generating file name
      const fileName = `
          ${getFileNameFromFileKey(fields.fileKey)}${getExtensionFromFileKey(
        fields.fileKey,
      )}`;

      const fileOwnerInfo = {
        userName: `${owner?.fields.given_name} ${owner?.fields.family_name}`,
        ...owner,
      };

      const item = {
        fileName,
        fileLocation: file.fields.path || '',
        channelId: selectedNotification.channelId,
        email: fileOwnerInfo.fields.email,
        fallbackColor: fileOwnerInfo.fields.fallbackColor,
        picture: fileOwnerInfo.fields.picture,
        ...fileOwnerInfo,
      };

      data.push(item);
    });

    setNotificationTableRowData(data);
  }, [selectedNotification, notificationsFromGroup]);

  React.useEffect(() => {
    generateNotificationTableRowData();
  }, [selectedNotification, notificationsFromGroup]);

  const unreadNotificationCount = getUnreadInboxNotificationCount(
    allInboxNotifications,
  );

  if (!selectedNotification) {
    return (
      <div className={classes.root}>
        <div>
          <div className={classes.notificationIcon}>
            <NotificationIcon />
          </div>
          <BaseTypography fontType="24Medium">Inbox</BaseTypography>
          <BaseTypography
            fontType="15Regular"
            color="textSecondary"
            className={classes.notificationCountDescription}
          >
            {unreadNotificationCount === 0
              ? 'No notifications'
              : `${unreadNotificationCount} unread notifications`}
          </BaseTypography>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classes.container}
      data-testid="inbox-selected-notification"
    >
      {notificationTableRowData && notificationTableRowData.length > 0 && (
        <InboxNotificationTable
          rowData={notificationTableRowData}
          notificationGroup={selectedNotification.notificationGroup}
        />
      )}
    </div>
  );
};
